import { computed, defineComponent, onMounted, reactive, set, watch, ref, onBeforeUnmount } from '@vue/composition-api';
import axios from 'axios';
import { hasRole } from '@/functions/authentication-functions';
import { alertDialogue, confirmDialogue, generalError, inputDialogue } from '@/functions/system-modal-functions';
import { SettingsContext } from '@/utilities/settings-context';
import { Company, Salesperson } from './admin-companies-types';
import type { LogoSource } from '@/views/company-info/company-info-types';
import { ConnectionStatus } from "@/views/connections/connections-types";
import { ModalIconType } from "@/components/system-modals/system-modals";
import Pagination from '@/components/pagination/pagination.vue';
import { debounce } from 'lodash';
import {
  enablePartialSponsoring as enablePartialSponsoringApi,
  disablePartialSponsoring as disablePartialSponsoringApi,
  setPartialSponsoringAvailablePoints as setPartialSponsoringAvailablePointsApi,
} from '@/functions/partial-sponsoring';
import JobylonConnection from "@/components/connection-jobylon/connection-jobylon.vue";
import TeamtailorConnection from "@/components/connection-teamtailor/connection-teamtailor.vue";
import TeamtailorXmlConnection from "@/components/connection-teamtailor-xml/connection-teamtailor-xml.vue";
import TeamtailorApiConnection from "@/components/connection-teamtailor-api/connection-teamtailor-api.vue";
import RecmanApiConnection from "@/components/connection-recman-api/connection-recman-api.vue";
import HaileyApiConnection from "@/components/connection-hailey-api/connection-hailey-api.vue";
import AdminCompanyCredits from "@/components/admin-company-credits/admin-company-credits.vue";
import AdminCompanyPromotions from "@/components/admin-company-promotions/admin-company-promotions.vue";
import {
  serviceNames,
  setDefaultPromotionType,
  defaultJobylonService,
  defaultPontyService,
  defaultIntensoService,
  defaultJobTipService,
  defaultTeamtailorService,
  defaultTeamtailorXmlService,
  defaultTeamtailorApiService,
  defaultRecmanApiService,
  defaultHaileyApiService,
  toggleAmsService as toggleAmsServiceApi,
  toggleJobFetchService as toggleJobFetchServiceApi,
  JobylonService,
  PontyService,
  IntensoService,
  JobTipService,
  TeamtailorService,
  TeamtailorXmlService,
  TeamtailorApiServiceInfo,
  RecmanApiServiceInfo,
  HaileyApiServiceInfo,
  AmsService,
  ServiceWithPromotionType,
  ServiceInfo,
  adminAddJobTip } from '@/functions/services-functions';
import { allPromotions, allPromotionsWithNone, PromotionType } from '@/states/promotion-state';
import { i18n } from '@/utilities/vue-i18n';
import CompanySubscriptions from '@/components/admin-company-subscriptions/admin-company-subscriptions.vue';
import PontyConnection from '@/components/connection-ponty/connection-ponty.vue';
import IntensoConnection from '@/components/connection-intenso/connection-intenso.vue';
import JobTipConnection from '@/components/connection-jobtip/connection-jobtip.vue';
import OutOfCreditsPromotion from '@/components/out-of-credits-promotion/out-of-credits-promotion.vue';

type ComponentCompany = Company & {
  searchName: string;
  searchOrganizationNumber: string;
  newPartialSponsoringPoints: number;
};

// Helper functions to get services by name from the services array
const getServicesByName = <T extends ServiceInfo['name']>(
  services: Company['services'],
  name: T): (
    T extends typeof serviceNames.jobylon ? JobylonService :
    T extends typeof serviceNames.ponty ? PontyService :
    T extends typeof serviceNames.intenso ? IntensoService :
    T extends typeof serviceNames.jobtip ? JobTipService :
    never
  )[] => {
  return services.filter(s => s.name === name) as unknown as any[];
};

const getServiceByName = <T extends (JobylonService | PontyService | IntensoService | JobTipService)>(services: T[], name: T['name']): T | undefined => {
  return services.find(s => s.name === name);
};

const serviceFilterRegex = /\s*s\s*:\s*(?<name>[^:\s]*)(?:(?::(?<options>\S+))?)\s*/gi;

const serviceOptionRegex = /(?<options>[^:]+)/gi;

const subscriptionFilterRegex = /\s*sub\s*:\s*(?<name>\S*)\s*/g;

const salespersonFilterRegex = /\s*sp\s*:\s*(?<name>\S*)\s*/g;

type ServiceFilters = {
  ams: 'connected' | 'waiting' | 'info' | 'waiting' | 'notconnected' | null;
  jobylon: never;
  teamtailor: never;
  ponty: never;
  intenso: never;
  jobtip: never;
  recman: never;
};

type ServiceNameFilter = keyof ServiceFilters;

const hasServiceFilter = (filter: string, service?: ServiceNameFilter) => {
  if (!service) {
    return Array.from(filter.matchAll(serviceFilterRegex)).length > 0;
  }

  return Array.from(filter.matchAll(serviceFilterRegex)).some(x => x.groups?.name === service);
};

const removeServiceFilter = (filter: string, service: ServiceNameFilter) => {
  const matches = Array.from(filter.matchAll(serviceFilterRegex));

  if (!service) {
    return matches.reduce((x, y) => x.replace(y[0], ''), filter);
  }

  const currentServiceMatch = matches.filter(x => x.groups?.name === service);

  if (currentServiceMatch.length === 0) {
    return filter;
  }

  return filter.replace(currentServiceMatch[0][0], '');
}

const removeFilters = (filter: string, ...services: ServiceNameFilter[]) => {
  return services.reduce((f, s) => removeServiceFilter(f, s), filter);
};

const getServiceOptions = <T extends ServiceNameFilter>(filter: string, service: T): ServiceFilters[T][] | null => {
  const matches = Array.from(filter.matchAll(serviceFilterRegex))
    .filter(x => x.groups?.name === service)
    .map(match => match.groups?.options ? match.groups.options.split(':') : [])
    .flat();

  return matches as ServiceFilters[T][];
};

// Convert short status names to ConnectionStatus values
const getConnectionStatusFromFilter = (statusFilter: ServiceFilters['ams'][]): ConnectionStatus[] => {
  return [...new Set(
    statusFilter.map(filter => {
      switch (filter?.toLowerCase()) {
        case 'connected':
          return [ConnectionStatus.connected];
        case 'waiting':
          return [ConnectionStatus.waitingForConnection];
        case 'info':
          return [ConnectionStatus.missingInformation, ConnectionStatus.wrongInformation];
        case 'notconnected':
          return [ConnectionStatus.notConnected];
        default:
          // Unknown status filter - return no matches to force specific status selection
          return [];
      }
    }).flat()
  )];
};

const hasSalespersonFilter = (filter: string) => Array.from(filter.matchAll(salespersonFilterRegex)).length > 0;

const getSalespersonFilter = (filter: string) => {
  const matches = Array.from(filter.matchAll(salespersonFilterRegex));
  return matches.length > 0 ? matches[0][1] : '';
};

const removeSalespersonFilter = (filter: string) => {
  const matches = Array.from(filter.matchAll(salespersonFilterRegex));
  return matches.length > 0 ? filter.replace(matches[0][0], '') : filter;
}

const hasSubscriptionFilter = (filter: string) => Array.from(filter.matchAll(subscriptionFilterRegex)).length > 0;

const removeSubscriptionFilter = (filter: string) => {
  const matches = Array.from(filter.matchAll(subscriptionFilterRegex));

  const currentServiceMatch = matches.filter(x => x.groups?.name === 'subscription');

  if (currentServiceMatch.length === 0) {
    return filter;
  }

  return filter.replace(currentServiceMatch[0][0], '');
}

export default defineComponent({
  components: {
    Pagination,
    JobylonConnection,
    AdminCompanyCredits,
    AdminCompanyPromotions,
    TeamtailorConnection,
    TeamtailorXmlConnection,
    TeamtailorApiConnection,
    RecmanApiConnection,
    HaileyApiConnection,
    CompanySubscriptions,
    PontyConnection,
    IntensoConnection,
    JobTipConnection,
    OutOfCreditsPromotion,
  },

  setup(_props, context) {
    const searchInput = ref<HTMLInputElement | null>(null);

    const settings = reactive({
      companies: [] as ComponentCompany[],
      partialSponsoringLoading: new Set<number>(),
      filter: '',
      selectedCompany: -1,
      jobylonModalOpen: false,
      pontyModalOpen: false,
      teamtailorModalOpen: false,
      recmanModalOpen: false,
      haileyModalOpen: false,
      creditModalOpen: false,
      intensoModalOpen: false,
      jobTipModalOpen: false,
      promotionsModalOpen: false,
      amsModalOpen: false,
    });

    const isMatching = (filter: string) => {
      const checkAms = hasServiceFilter(filter, 'ams');
      const amsStatusFilter = getServiceOptions(filter, 'ams');
      const allowedAmsStatuses = amsStatusFilter ? getConnectionStatusFromFilter(amsStatusFilter) : [];
      const checkJobylon = hasServiceFilter(filter, 'jobylon');

      const checkjobTip = hasServiceFilter(filter, 'jobtip');

      const checkTeamtailor = hasServiceFilter(filter, 'teamtailor');

      const checkPonty = hasServiceFilter(filter, 'ponty');
      const checkIntenso = hasServiceFilter(filter, 'intenso');
      const checkRecman = hasServiceFilter(filter, 'recman');

      const checkSalesperson = hasSalespersonFilter(filter);

      const salespersonFilter = getSalespersonFilter(filter);

      const subscriptionFilter = hasSubscriptionFilter(filter);

      filter = filter.trim();
      filter = removeSalespersonFilter(removeFilters(filter, 'ams', 'jobylon', 'teamtailor', 'ponty', 'intenso', 'jobtip', 'recman'));

      return (company: ComponentCompany) => {
        return (!checkAms || allowedAmsStatuses.length === 0 || (company.amsService && allowedAmsStatuses.includes(company.amsService.status))) &&
          (!checkJobylon || getServicesByName(company.services, serviceNames.jobylon).some(s => s.status === ConnectionStatus.connected)) &&
          (!checkPonty || getServicesByName(company.services, serviceNames.ponty).some(s => s.status === ConnectionStatus.connected)) &&
          (!checkIntenso || getServicesByName(company.services, serviceNames.intenso).some(s => s.status === ConnectionStatus.connected)) &&
          (!checkjobTip || getServicesByName(company.services, serviceNames.jobtip).some(s => s.status === ConnectionStatus.connected)) &&
          (!checkTeamtailor || company.teamtailorService?.status === ConnectionStatus.connected || company.teamtailorXmlService?.status === ConnectionStatus.connected) &&
          (!checkRecman || company.recmanApiService?.status === ConnectionStatus.connected) &&
          (!checkSalesperson || company.salespersons.some(sp => sp.name.toLowerCase() == salespersonFilter)) &&
          (!subscriptionFilter || company.hasSubscriptions) &&
          (
            company.searchName.includes(filter) === true ||
            company.searchOrganizationNumber.includes(filter) === true ||
            company.salespersons.some(x => x.name.toLowerCase().includes(filter)) === true
          );
      };
    };

    const filteredCompanies = computed(() => {
      const value = settings.companies;

      if (settings.filter) {
        return value.filter(isMatching(settings.filter.toLowerCase()));
      }

      return value;
    });

    const currentActiveCompany = computed<ComponentCompany | undefined>(() => filteredCompanies.value[settings.selectedCompany]);

    // Computed properties for current active company services by type
    const currentJobylonServices = computed(() =>
      currentActiveCompany.value ? getServicesByName(currentActiveCompany.value.services, serviceNames.jobylon) : []
    );
    const currentPontyServices = computed(() =>
      currentActiveCompany.value ? getServicesByName(currentActiveCompany.value.services, serviceNames.ponty) : []
    );
    const currentIntensoServices = computed(() =>
      currentActiveCompany.value ? getServicesByName(currentActiveCompany.value.services, serviceNames.intenso) : []
    );
    const currentJobTipServices = computed(() =>
      currentActiveCompany.value ? getServicesByName(currentActiveCompany.value.services, serviceNames.jobtip) : []
    );

    const pagination = reactive({
      current: 1,
      perPage: 20,
      total: computed(() => filteredCompanies.value.length),
      start: 0,
      end: 20,
    });

    watch(() => settings.filter, () => {
      pagination.current = 1;
      pagination.start = 0;
      pagination.end = pagination.perPage;
      context.root.$router.push({ query: { search: settings.filter } }).catch(() => {
        // ignored
      });
    });

    const chunkCompanies = (start: number, end: number) => {
      pagination.start = start;
      pagination.end = end;
    };

    const getSummaryName = (fullName: string) => {
      const names = fullName.split(' ');
      if (names.length > 1) {
        return `${names[0]}-${names[1].charAt(0)}.`;
      }
      return fullName;
    }

    const getCompanies = async () => {
      const response = await axios.get<Company[]>(SettingsContext.getApiUrl('dashboardGetCompanies'));

      const sortedData = response.data.sort((a: Company, b: Company) => a.name < b.name ? -1 : 1);

      settings.companies = sortedData.map((x: Company) => {
        // Ensure at least one of each service type or default
        const services = x.services && x.services.length > 0 ? [...x.services] : [];
        if (!services.some(s => s.name === serviceNames.jobylon)) {
          services.push(defaultJobylonService());
        }
        if (!services.some(s => s.name === serviceNames.ponty)) {
          services.push(defaultPontyService());
        }
        if (!services.some(s => s.name === serviceNames.intenso)) {
          services.push(defaultIntensoService());
        }
        if (!services.some(s => s.name === serviceNames.jobtip)) {
          services.push(defaultJobTipService());
        }

        return {
          ...x,
          forceIncomingExternalPostingAsDraft: !!x.forceIncomingExternalPostingAsDraft,
          ssoEnabled: !!x.ssoEnabled,
          salespersons: x.salespersons.map((sp: Salesperson) => ({ ...sp, name: getSummaryName(sp.name) })) ?? [],
          newPartialSponsoringPoints: x.partialSponsoringPoints ?? 1,
          searchName: x.name.toLowerCase(),
          searchOrganizationNumber: x.organizationNumber.toLowerCase(),
          services,
          teamtailorService: x.teamtailorService ?? defaultTeamtailorService(),
          teamtailorXmlService: x.teamtailorXmlService ?? defaultTeamtailorXmlService(),
          teamtailorApiService: x.teamtailorApiService ?? defaultTeamtailorApiService(),
          recmanApiService: x.recmanApiService ?? defaultRecmanApiService(),
          haileyApiService: x.haileyApiService ?? defaultHaileyApiService(),
        };
      });
    };

    const isUserAdmin = computed(() => hasRole('Administrator'));

    const isUserEconomy = computed(() => hasRole('Economy'));

    const isUserModerator = computed(() => hasRole('Moderator'));

    const canChangeMatching = computed(() => isUserAdmin.value || isUserModerator.value || hasRole('InternalSales'));

    const toggleForceIncomingExternalDraft = async (company: Company) => {
      if (!isUserAdmin.value && !isUserModerator.value) {
        return;
      }

      const next = !company.forceIncomingExternalPostingAsDraft;

      try {
        await axios.post(SettingsContext.getApiUrl('setForceIncomingExternalPostingAsDraft'), {
          id: company.id,
          forceIncomingExternalPostingAsDraft: next,
        });

        company.forceIncomingExternalPostingAsDraft = next;
      } catch {
        await generalError();
      }
    };

    const toggleJobFetchService = async (company: Company) => {
      if (!isUserAdmin.value && !isUserModerator.value) {
        return;
      }

      const next = !company.hasJobFetchService;

      try {
        await toggleJobFetchServiceApi(company.id, next);
        company.hasJobFetchService = next;
      } catch {
        await generalError();
      }
    };

    const toggleIgnoreDemandSlots = async (company: Company) => {
      if (!isUserAdmin.value && !isUserModerator.value) {
        return;
      }

      const next = !company.ignoreDemandSlots;

      try {
        await axios.post(SettingsContext.getApiUrl('setIgnoreDemandSlots'), {
          id: company.id,
          ignoreDemandSlots: next,
        });

        company.ignoreDemandSlots = next;
      } catch {
        await generalError();
      }
    };

    const setLogoSource = async (company: Company, value: LogoSource) => {
      if (!isUserAdmin.value && !isUserModerator.value) {
        return;
      }

      const previous = company.logoSource;
      company.logoSource = value;

      try {
        await axios.post(SettingsContext.getApiUrl('adminSetCompanyLogoSource'), {
          id: company.id,
          logoSource: value,
        });
      } catch {
        company.logoSource = previous;
        await generalError();
      }
    };

    const toggleSsoEnabled = async (company: Company) => {
      if (!isUserAdmin.value && !isUserModerator.value) {
        return;
      }

      const previous = company.ssoEnabled;
      const next = !previous;
      company.ssoEnabled = next;

      try {
        await axios.post(SettingsContext.getApiUrl('setSsoEnabled'), {
          id: company.id,
          ssoEnabled: next,
        });
      } catch {
        company.ssoEnabled = previous;
        await generalError();
      }
    };

    const focusSearch = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.keyCode === 70) {
        e.preventDefault();
        searchInput.value?.focus();
      }
    };

    // In-flight guard, keyed by company id.
    const matchingPending = reactive<Record<string, boolean>>({});

    onMounted(async () => {
      if (isUserEconomy.value || canChangeMatching.value) {

        await getCompanies();
      }
      window.addEventListener('keydown', focusSearch);
      if ('search' in context.root.$route.query) {
        settings.filter = context.root.$route.query.search as string;
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener('keydown', focusSearch);
    });

    const companyUpdateVat = async (id: number, vat: boolean) => {
      try {
        await axios.post(SettingsContext.getApiUrl('UpdateCompanyVat'), { id, vat });
      }
      catch {
        await generalError();
      }
    };

    const companyUpdateMatching = async (company: Company, enabled: boolean, event: Event) => {
      if (matchingPending[company.id]) {
        return;
      }
      set(matchingPending, String(company.id), true);
      try {
        // Body key is `id`: the API model inherits ForCompanyModel, like the VAT toggle.
        await axios.post(SettingsContext.getApiUrl('UpdateCompanyMatching'), { id: company.id, enabled });
        company.matchingEnabled = enabled;
      }
      catch {
        (event.target as HTMLInputElement).checked = !!company.matchingEnabled;
        await generalError();
      }
      finally {
        set(matchingPending, String(company.id), false);
      }
    };

    const toggleCompanyBan = async (company: Company) => {

      if (!isUserAdmin.value && !isUserModerator.value) {

        return;
      }

      if (await confirmDialogue(company.banned ? 'Company Unban' : 'Company Ban', `Are you sure you want to <b class="${company.banned ? 'text-success' : 'text-danger'}">${company.banned ? 'unban' : 'ban'}</b> the company <b>${company.name}</b>?`, ModalIconType.question)) {

        try {

          await axios.post(SettingsContext.getApiUrl('setBan'), { id: company.id, banned: !company.banned });

          company.banned = !company.banned;

        } catch {

          await generalError();
        }

      }
    }

    const addSalesperson = async (company: Company) => {

      try {

        const response = await axios.post(SettingsContext.getApiUrl('addSalespersonToCompanyRecruiters'), { companyId: company.id });

        const salesperson = response.data;

        const companyToUpdate = settings.companies.find((x: ComponentCompany) => x.id === company.id);

        if (companyToUpdate) companyToUpdate.salespersons.push({
          name: getSummaryName(salesperson.name),
          id: salesperson.id,
          email: salesperson.email,
          removable: true,
          isMain: companyToUpdate.salespersons.length === 0
        });

      } catch {

        await generalError();

      }
    };

    const removeSalesperson = async (company: Company) => {

      if (await confirmDialogue(i18n.t("dialogue.remove-title"), i18n.t("dialogue.remove"), ModalIconType.question)) {
        try {

          const response = await axios.post(SettingsContext.getApiUrl('removeSalespersonFromCompanyRecruiters'), { companyId: company.id });

          const companyToUpdate = settings.companies.find((x: ComponentCompany) => x.id === company.id);

          if (companyToUpdate) {

            companyToUpdate.salespersons = companyToUpdate.salespersons.filter((x: Salesperson) => !x.removable);
            companyToUpdate.salespersons.forEach((x: Salesperson) => x.isMain = x.id === response.data.mainSalespersonId);
          }

        } catch {

          await generalError();

        }
      }
    };

    const addSenderId = async (company: Company, enable: boolean) => {

      if (!isUserAdmin.value && !isUserModerator.value) {

        return;
      }

      if (enable && !(await confirmDialogue('Manual SenderId Input', `Are you sure you want to manually set the status to <b>pending</b> and set the <b>SenderId</b> for <b>${company.name} (${company.organizationNumber})</b>?`, ModalIconType.question))) {

        return;
      }

      const result = await inputDialogue({
        title: 'SenderId',
        message: `SenderId for ${company.name} (${company.organizationNumber}):`,
        placeholder: 'SenderId',
        validation: input => /^\d{8}$/.test(input),
        initialInputData: '',
        icon: ModalIconType.question
      });

      if (result.confirmed) {

        try {

          await axios.post(SettingsContext.getApiUrl('addAmsSenderId'), { companyId: company.id, senderId: result.input, manuallyEnable: enable });

          await getCompanies();
        }
        catch {
          await generalError();
        }
      }
    }

    const enablePartialSponsoring = async (company: Company) => {
      settings.partialSponsoringLoading.add(company.id);

      try {
        await enablePartialSponsoringApi(company.id);
        company.partialSponsoringEnabled = true;
      } catch {
        await generalError();
      }

      settings.partialSponsoringLoading.delete(company.id);
    };

    const disablePartialSponsoring = async (company: Company) => {
      settings.partialSponsoringLoading.add(company.id);

      try {
        await disablePartialSponsoringApi(company.id);
        company.partialSponsoringEnabled = false;
      } catch {
        await generalError();
      }

      settings.partialSponsoringLoading.delete(company.id);
    };

    const setPartialSponsoringAvailablePoints = debounce(async (company: ComponentCompany) => {
      if (+company.newPartialSponsoringPoints < 0 || +company.newPartialSponsoringPoints > 4294967295) {
        await alertDialogue('invalid points assigned', 'points should be between 0 and 4294967295');
        return;
      }

      settings.partialSponsoringLoading.add(company.id);

      try {
        await setPartialSponsoringAvailablePointsApi(company.id, +company.newPartialSponsoringPoints);
        company.partialSponsoringPoints = +company.newPartialSponsoringPoints;
      } catch {
        await generalError();
      }

      settings.partialSponsoringLoading.delete(company.id);
    }, 1000);

    const togglePartialSponsoring = debounce(async (company: Company) => {
      if (company.partialSponsoringEnabled) {
        await disablePartialSponsoring(company);
      } else {
        await enablePartialSponsoring(company);
      }
    }, 1000);

    const updateJobylonService = (company: Company, service: JobylonService | null, oldService: JobylonService) => {
      const foundServiceIndex = company.services.findIndex(x => x.id === oldService.id && x.name === serviceNames.jobylon);

      if (foundServiceIndex !== -1) {
        if (service !== null) {
          company.services[foundServiceIndex] = service;
        }
        else {
          company.services = company.services.filter((_, i) => i !== foundServiceIndex);
        }
      }
      else {
        if (service !== null) {
          company.services.push(service);
        }
      }

      // Ensure at least one default service for UI
      const hasJobylon = company.services.some(s => s.name === serviceNames.jobylon);
      if (!hasJobylon) {
        company.services.push(defaultJobylonService());
      }

      company.services = [...company.services];
    };

    const updatePontyService = (company: Company, service: PontyService | null, oldService: PontyService) => {
      const foundServiceIndex = company.services.findIndex(x => x.id === oldService.id && x.name === serviceNames.ponty);

      if (foundServiceIndex !== -1) {
        if (service !== null) {
          company.services[foundServiceIndex] = service;
        }
        else {
          company.services = company.services.filter((_, i) => i !== foundServiceIndex);
        }
      }
      else {
        if (service !== null) {
          company.services.push(service);
        }
      }

      // Ensure at least one default service for UI
      const hasPonty = company.services.some(s => s.name === serviceNames.ponty);
      if (!hasPonty) {
        company.services.push(defaultPontyService());
      }

      company.services = [...company.services];
    };

    const updateIntensoService = (company: Company, service: IntensoService | null, oldService: IntensoService) => {
      const foundServiceIndex = company.services.findIndex(x => x.id === oldService.id && x.name === serviceNames.intenso);
      if (foundServiceIndex !== -1) {
        if (service !== null) {
          company.services[foundServiceIndex] = service;
        } else {
          company.services = company.services.filter((_, i) => i !== foundServiceIndex);
        }
      } else if (service !== null) {
        company.services.push(service);
      }

      // Ensure at least one default service for UI
      const hasIntenso = company.services.some(s => s.name === serviceNames.intenso);
      if (!hasIntenso) {
        company.services.push(defaultIntensoService());
      }

      company.services = [...company.services];
    };

    const updateJobTipService = (company: Company, service: JobTipService | null, oldService: JobTipService) => {
      const foundServiceIndex = company.services.findIndex(x => x.id === oldService.id && x.name === serviceNames.jobtip);
      if (foundServiceIndex !== -1) {
        if (service !== null) {
          company.services[foundServiceIndex] = service;
        } else {
          company.services = company.services.filter((_, i) => i !== foundServiceIndex);
        }
      } else if (service !== null) {
        company.services.push(service);
      }
      company.services = [...company.services];
    };

    const updateTeamtailorService = (company: Company, service: TeamtailorService | null) => {
      company.teamtailorService = service ?? defaultTeamtailorService();
    };

    const updateTeamtailorXmlService = (company: Company, service: TeamtailorXmlService | null) => {
      company.teamtailorXmlService = service ?? defaultTeamtailorXmlService();
    };

    const updateTeamtailorApiService = (company: Company, service: TeamtailorApiServiceInfo | null) => {
      company.teamtailorApiService = service ?? defaultTeamtailorApiService();
    };

    const updateRecmanApiService = (company: Company, service: RecmanApiServiceInfo | null) => {
      const value = service ?? defaultRecmanApiService();
      set(company, 'recmanApiService', value);
    };

    const updateHaileyApiService = (
      company: Company,
      service: HaileyApiServiceInfo | null
    ) => {
      set(company, 'haileyApiService', service ?? defaultHaileyApiService());
    };

    const toggleAmsRequiredService = async (company: Company) => {
      const currentRequired = company.amsService?.required ?? false;

      const updatedService = await toggleAmsServiceApi(company.id, !currentRequired, undefined);

      if (updatedService) {
        company.amsService = updatedService;
      }
    };

    const toggleAmsActiveService = async (company: Company) => {
      const currentActive = company.amsService?.active ?? false;

      const updatedService = await toggleAmsServiceApi(company.id, undefined, !currentActive);

      if (updatedService) {
        company.amsService = updatedService;
      }
    };

    const addAmsStatusFilter = (status: ServiceFilters['ams']) => {
      // Remove any existing AMS filters first
      if (hasServiceFilter(settings.filter, 'ams')) {
        settings.filter = removeServiceFilter(settings.filter, 'ams');
        return;
      }

      // Add the new AMS status filter
      settings.filter = settings.filter ? `s: ams:${status} ` + settings.filter : `s:ams:${status}`;
    };

    const addJobylonFilter = () => {
      if (hasServiceFilter(settings.filter, 'jobylon')) {
        settings.filter = removeServiceFilter(settings.filter, 'jobylon');
        return;
      }

      settings.filter = settings.filter ? 's: jobylon ' + settings.filter : 's: jobylon';
    }

    const addPontyFilter = () => {
      if (hasServiceFilter(settings.filter, 'ponty')) {
        settings.filter = removeServiceFilter(settings.filter, 'ponty');
        return;
      }

      settings.filter = settings.filter ? 's: ponty ' + settings.filter : 's: ponty';
    }

    const addIntensoFilter = () => {
      if (hasServiceFilter(settings.filter, 'intenso')) {
        settings.filter = removeServiceFilter(settings.filter, 'intenso');
        return;
      }
      settings.filter = settings.filter ? 's: intenso ' + settings.filter : 's: intenso';
    }

    const addJobTipFilter = () => {
      if (hasServiceFilter(settings.filter, 'jobtip')) {
        settings.filter = removeServiceFilter(settings.filter, 'jobtip');
        return;
      }
      settings.filter = settings.filter ? 's: jobtip ' + settings.filter : 's: jobtip';
    }

    const addRecmanFilter = () => {
      if (hasServiceFilter(settings.filter, 'recman')) {
        settings.filter = removeServiceFilter(settings.filter, 'recman');
        return;
      }
      settings.filter = settings.filter ? 's: recman ' + settings.filter : 's: recman';
    }

    const addTeamtailorFilter = () => {
      if (hasServiceFilter(settings.filter, 'teamtailor')) {
        settings.filter = removeServiceFilter(settings.filter, 'teamtailor');
        return;
      }

      settings.filter = settings.filter ? 's: teamtailor ' + settings.filter : 's: teamtailor';
    }

    const addJobylonConnection = (company: Company) => {
      company.services.push(defaultJobylonService());
    };

    const addTeamtailorConnection = (company: Company) => {
      company.teamtailorService = company.teamtailorService ?? defaultTeamtailorService();
    };

    const addTeamtailorXmlConnection = (company: Company) => {
      company.teamtailorXmlService = company.teamtailorXmlService ?? defaultTeamtailorXmlService();
    };

    const addPontyConnection = (company: Company) => {
      company.services.push(defaultPontyService());
    };

    const addIntensoConnection = (company: Company) => {
      company.services.push(defaultIntensoService());
    };

    const addJobTipConnection = async (company: Company) => {
      company.services.push(defaultJobTipService());
    };

    const openPontyModal = (companyIndex: number) => {
      settings.pontyModalOpen = true;
      settings.selectedCompany = companyIndex + pagination.start;
    };

    const closePontyModal = () => {
      settings.selectedCompany = -1;
      settings.pontyModalOpen = false;
    };
    const openIntensoModal = (companyIndex: number) => {
      settings.intensoModalOpen = true;
      settings.selectedCompany = companyIndex + pagination.start;
    };
    const closeIntensoModal = () => {
      settings.selectedCompany = -1;
      settings.intensoModalOpen = false;
    };

    const openJobTipModal = (companyIndex: number) => {
      settings.jobTipModalOpen = true;
      settings.selectedCompany = companyIndex + pagination.start;
    };

    const closeJobTipModal = () => {
      settings.selectedCompany = -1;
      settings.jobTipModalOpen = false;
    };

    const openAmsModal = (companyIndex: number) => {
      settings.amsModalOpen = true;
      settings.selectedCompany = companyIndex + pagination.start;
    };

    const closeAmsModal = () => {
      settings.selectedCompany = -1;
      settings.amsModalOpen = false;
    };

    const openJobylonModal = (companyIndex: number) => {
      settings.jobylonModalOpen = true;
      settings.selectedCompany = companyIndex + pagination.start;
    };

    const closeJobylonModal = () => {
      settings.selectedCompany = -1;
      settings.jobylonModalOpen = false;
    };

    const openTeamtailorModal = (companyIndex: number) => {
      settings.teamtailorModalOpen = true;
      settings.selectedCompany = companyIndex + pagination.start;
    };

    const closeTeamtailorModal = () => {
      settings.selectedCompany = -1;
      settings.teamtailorModalOpen = false;
    };

    const openRecmanModal = (companyIndex: number) => {
      settings.recmanModalOpen = true;
      settings.selectedCompany = companyIndex + pagination.start;
    };

    const closeRecmanModal = () => {
      settings.selectedCompany = -1;
      settings.recmanModalOpen = false;
    };

    const openHaileyModal = (companyIndex: number) => {
      settings.haileyModalOpen = true;
      settings.selectedCompany = companyIndex + pagination.start;
    };

    const closeHaileyModal = () => {
      settings.selectedCompany = -1;
      settings.haileyModalOpen = false;
    };

    const openCreditsModal = (companyIndex: number) => {
      settings.creditModalOpen = true;
      settings.selectedCompany = companyIndex + pagination.start;
    };

    const closeCreditsModal = () => {
      settings.selectedCompany = -1;
      settings.creditModalOpen = false;
    };

    const setPromotionType = async (service: ServiceWithPromotionType | null, type: PromotionType | null) => {
      if (!service || !service.id || service.status !== ConnectionStatus.connected) {
        return;
      }

      if (await setDefaultPromotionType(service.id, type)) {
        service.defaultPromotionType = type;
      }
      else {
        await generalError();
      }

    };

    const canAddMoreSalesperson = (company: Company) => {
      return company.salespersons.length > 0 && company.salespersons.every(x => !x.removable);
    }

    const filterBySalesperson = (salesperson?: Salesperson) => {
      if (salesperson) {
        settings.filter = `sp: ${salesperson.name}`;
      } else {
        settings.filter = '';
      }
    }

    const showSalespersons = (event: Event) => {

      event.preventDefault();

      const uniqueSalespersons = settings
        .companies
        .flatMap((x: ComponentCompany) => x.salespersons)
        .filter((person: Salesperson, index: number, self: Salesperson[]) => index === self.findIndex(p => p.id === person.id))
        .sort((a: Salesperson, b: Salesperson) => a.name.localeCompare(b.name));

      if (uniqueSalespersons.length === 0) return;

      const dropdownBtnElm = event.target as HTMLElement;
      const dropdownListElm = dropdownBtnElm.parentElement?.querySelector('[aria-labelledby="salespersonDropdown"]');

      dropdownListElm?.querySelectorAll('li').forEach(x => x.remove());

      const createItem = (salesperson?: Salesperson) => {
        const salespersonElm = document.createElement('li');
        const salespersonLinkElm = document.createElement('a');
        salespersonLinkElm.classList.add('dropdown-item');
        salespersonLinkElm.href = '#';
        salespersonLinkElm.innerText = salesperson ? salesperson.name : i18n.t('pages.admin-companies.salesperson-none');
        salespersonLinkElm.title = salesperson?.email ?? '';
        salespersonLinkElm.addEventListener('click', () => filterBySalesperson(salesperson));
        salespersonElm.appendChild(salespersonLinkElm);
        dropdownListElm?.appendChild(salespersonElm);
      };

      createItem();
      uniqueSalespersons.forEach((salesperson: Salesperson) => { createItem(salesperson); });
    }

    const changeSalesperson = async (companyId: number, salespersonId: string) => {

      try {

        await axios.post(SettingsContext.getApiUrl('changeMainSalespersonInCompany'), { companyId, userId: salespersonId });

        settings.companies.find((x: ComponentCompany) => x.id === companyId)?.salespersons.forEach((x: Salesperson) => x.isMain = x.id === salespersonId);

      } catch {

        await generalError();

      }

    }

    const subscriptionModalOpen = ref(false);
    const selectedCompanyForSubscriptions = ref<Company | null>(null);

    const addSubscriptionFilter = () => {
      if (hasSubscriptionFilter(settings.filter)) {
        settings.filter = removeSubscriptionFilter(settings.filter);
        return;
      }
      settings.filter = settings.filter ? 'sub: subscription ' + settings.filter : 'sub: subscription';
    };

    const openSubscriptionsModal = async (company: Company) => {
      selectedCompanyForSubscriptions.value = company;
      subscriptionModalOpen.value = true;
    };

    const openPromotionsModal = (companyIndex: number) => {
      settings.promotionsModalOpen = true;
      settings.selectedCompany = companyIndex + pagination.start;
    };

    const closePromotionsModal = () => {
      settings.selectedCompany = -1;
      settings.promotionsModalOpen = false;
    };

    const updatePromotions = (promotions: PromotionType[]) => {
      const company = currentActiveCompany.value;
      if (!company) return;
      company.includedPackages = [...promotions];
    }

    // Service status helpers
    const isConnected = (status?: ConnectionStatus) => status === ConnectionStatus.connected;
    const areAllServicesConnected = <T extends { status?: ConnectionStatus | null }>(services: T[]) =>
      services.every(service => isConnected(service?.status ?? undefined));

    const amsConnected = (company: ComponentCompany) => isConnected(company.amsService?.status);
    const pontyConnectedCount = (company: ComponentCompany) => getServicesByName(company.services, serviceNames.ponty).filter(s => isConnected(s.status)).length;
    const jobylonConnectedCount = (company: ComponentCompany) => getServicesByName(company.services, serviceNames.jobylon).filter(s => isConnected(s.status)).length;
    const teamtailorAnyConnected = (company: ComponentCompany) => isConnected(company.teamtailorService?.status) || isConnected(company.teamtailorXmlService?.status) || isConnected(company.teamtailorApiService?.status);
    const recmanAnyConnected = (company: ComponentCompany) => isConnected(company.recmanApiService?.status);
    const haileyAnyConnected = (company: ComponentCompany) =>
      isConnected(company.haileyApiService?.status);
    const intensoAnyConnected = (company: ComponentCompany) => getServicesByName(company.services, serviceNames.intenso).some(s => isConnected(s.status));
    const jobTipAnyConnected = (company: ComponentCompany) => getServicesByName(company.services, serviceNames.jobtip).some(s => isConnected(s.status));

    const aggregateServicesLabel = (company: ComponentCompany) => {
      const parts: string[] = [];
      if (amsConnected(company)) parts.push('AMS');
      if (pontyConnectedCount(company) > 0) parts.push(`Ponty:${pontyConnectedCount(company)}`);
      if (jobylonConnectedCount(company) > 0) parts.push(`Jobylon:${jobylonConnectedCount(company)}`);
      if (teamtailorAnyConnected(company)) parts.push('Teamtailor');
      if (recmanAnyConnected(company)) parts.push('Recman');
      if (haileyAnyConnected(company)) {
        parts.push('Hailey');
      }
      if (intensoAnyConnected(company)) parts.push('Intenso');
      if (jobTipAnyConnected(company)) parts.push('JobTip');
      if (parts.length === 0) return i18n.t('general.no').toString();
      return parts.join(' | ');
    };

    const errorStatuses: ConnectionStatus[] = [
      ConnectionStatus.waitingForConnection,
      ConnectionStatus.wrongInformation,
      ConnectionStatus.missingInformation,
    ];
    const aggregateServicesBtnClass = (company: ComponentCompany) => {
      const amsError = company.amsService && errorStatuses.includes(company.amsService.status);
      const pontyServices = getServicesByName(company.services, serviceNames.ponty);
      const jobylonServices = getServicesByName(company.services, serviceNames.jobylon);
      const intensoServices = getServicesByName(company.services, serviceNames.intenso);
      const jobTipServices = getServicesByName(company.services, serviceNames.jobtip);
      const anyErrorPonty = pontyServices.some(s => errorStatuses.includes(s.status));
      const anyErrorJobylon = jobylonServices.some(s => errorStatuses.includes(s.status));
      const teamtailorError = [company.teamtailorService, company.teamtailorXmlService, company.teamtailorApiService].some(s => s && errorStatuses.includes(s.status));
      const recmanError = company.recmanApiService && errorStatuses.includes(company.recmanApiService.status);
      const haileyError = company.haileyApiService &&
        errorStatuses.includes(company.haileyApiService.status);
      const intensoError = intensoServices.some(s => errorStatuses.includes(s.status));
      const jobTipError = jobTipServices.some(s => errorStatuses.includes(s.status));
      const hasAnyConnected =
        amsConnected(company) ||
        pontyConnectedCount(company) + jobylonConnectedCount(company) > 0 ||
        teamtailorAnyConnected(company) ||
        recmanAnyConnected(company) ||
        haileyAnyConnected(company) ||
        intensoAnyConnected(company) ||
        jobTipAnyConnected(company);

      if (
        amsError ||
        anyErrorPonty ||
        anyErrorJobylon ||
        teamtailorError ||
        recmanError ||
        haileyError ||
        intensoError ||
        jobTipError
      ) {
        return 'btn-danger';
      }

      return hasAnyConnected ? 'btn-success' : 'btn-action';
    };

    // Individual service labels & classes
    const pontyLabel = (company: ComponentCompany) => pontyConnectedCount(company) > 0 ? `${i18n.t('general.yes')}: ${pontyConnectedCount(company)}` : i18n.t('general.no').toString();
    const jobylonLabel = (company: ComponentCompany) => jobylonConnectedCount(company) > 0 ? `${i18n.t('general.yes')}: ${jobylonConnectedCount(company)}` : i18n.t('general.no').toString();
    const teamtailorLabel = (company: ComponentCompany) => {
      const connected: string[] = [];
      if (isConnected(company.teamtailorService?.status)) connected.push(i18n.t('views.admin-companies.webhook').toString());
      if (isConnected(company.teamtailorXmlService?.status)) connected.push(i18n.t('views.admin-companies.xml').toString());
      if (isConnected(company.teamtailorApiService?.status)) connected.push('API');
      return connected.length > 0 ? connected.join(', ') : i18n.t('general.no').toString();
    };
    const intensoLabel = (company: ComponentCompany) => intensoAnyConnected(company) ? i18n.t('general.yes').toString() : i18n.t('general.no').toString();
    const jobTipLabel = (company: ComponentCompany) => jobTipAnyConnected(company) ? i18n.t('general.yes').toString() : i18n.t('general.no').toString();
    const amsLabel = (company: ComponentCompany) => amsConnected(company) ? i18n.t('general.yes').toString() : i18n.t('general.no').toString();

    const serviceBtnClassPonty = (company: ComponentCompany) => {
      const pontyServices = getServicesByName(company.services, serviceNames.ponty);
      if (pontyServices.length === 0) return 'btn-action';
      if (pontyServices.some(s => errorStatuses.includes(s.status))) return 'btn-danger';
      return pontyConnectedCount(company) > 0 ? 'btn-success' : 'btn-action';
    };
    const serviceBtnClassJobylon = (company: ComponentCompany) => {
      const jobylonServices = getServicesByName(company.services, serviceNames.jobylon);
      if (jobylonServices.length === 0) return 'btn-action';
      if (jobylonServices.some(s => errorStatuses.includes(s.status))) return 'btn-danger';
      return jobylonConnectedCount(company) > 0 ? 'btn-success' : 'btn-action';
    };
    const serviceBtnClassTeamtailor = (company: ComponentCompany) => {
      const services = [company.teamtailorService, company.teamtailorXmlService, company.teamtailorApiService].filter(Boolean) as any[];
      if (services.length === 0) return 'btn-action';
      if (services.some(s => s.status && errorStatuses.includes(s.status))) return 'btn-danger';
      return teamtailorAnyConnected(company) ? 'btn-success' : 'btn-action';
    };
    const intensoBadgeClass = (company: ComponentCompany) => intensoAnyConnected(company) ? 'badge-success' : 'badge-secondary';
    const serviceBtnClassIntenso = (company: ComponentCompany) => {
      const intensoServices = getServicesByName(company.services, serviceNames.intenso);
      if (intensoServices.length === 0) return 'btn-action';
      if (intensoServices.some(s => errorStatuses.includes(s.status))) return 'btn-danger';
      return intensoAnyConnected(company) ? 'btn-success' : 'btn-action';
    };

    const serviceBtnClassJobTip = (company: ComponentCompany) => {
      const jobTipServices = getServicesByName(company.services, serviceNames.jobtip);
      if (jobTipServices.length === 0) return 'btn-action';
      if (jobTipServices.some(s => errorStatuses.includes(s.status))) return 'btn-danger';
      return jobTipAnyConnected(company) ? 'btn-success' : 'btn-action';
    };

    // AMS helpers
    const amsBadgeClass = (company: ComponentCompany) => {
      if (company.amsService && errorStatuses.includes(company.amsService.status)) return 'badge-danger';
      return amsConnected(company) ? 'badge-success' : 'badge-secondary';
    };

    const serviceBtnClassAms = (company: ComponentCompany) => {
      if (company.amsService && errorStatuses.includes(company.amsService.status)) return 'btn-danger';
      return amsConnected(company) ? 'btn-success' : 'btn-action';
    };

    return {
      searchInput,

      pagination,

      togglePartialSponsoring,

      setPartialSponsoringAvailablePoints,

      chunkCompanies,

      settings,

      filteredCompanies,

      companyUpdateVat,
      companyUpdateMatching,
      matchingPending,

      isUserAdmin,

      isUserEconomy,

      isUserModerator,
      canChangeMatching,

      ConnectionStatus,
      areAllServicesConnected,

      addSenderId,

      toggleCompanyBan,

      updateJobylonService,

      addJobylonFilter,

      addJobylonConnection,

      openJobylonModal,

      closeJobylonModal,

      updateTeamtailorService,

      updateTeamtailorXmlService,

      updateTeamtailorApiService,

      updateRecmanApiService,

      updateHaileyApiService,

      toggleAmsRequiredService,
      toggleAmsActiveService,

      addTeamtailorFilter,

      addTeamtailorConnection,

      addTeamtailorXmlConnection,

      openTeamtailorModal,

      closeTeamtailorModal,

      openRecmanModal,

      closeRecmanModal,

      openHaileyModal,

      closeHaileyModal,

      openCreditsModal,

      closeCreditsModal,

      setPromotionType,

      allPromotions,

      allPromotionsWithNone,

      addSalesperson,

      removeSalesperson,

      canAddMoreSalesperson,

      showSalespersons,

      changeSalesperson,

      currentActiveCompany,

      currentJobylonServices,
      currentPontyServices,
      currentIntensoServices,
      currentJobTipServices,

      subscriptionModalOpen,

      selectedCompanyForSubscriptions,

      addSubscriptionFilter,

      openSubscriptionsModal,

      openPromotionsModal,

      closePromotionsModal,

      updatePromotions,

      addPontyFilter,

      openPontyModal,

      closePontyModal,

      updatePontyService,
      updateIntensoService,

      addPontyConnection,

      addAmsStatusFilter,
      addIntensoFilter,
      // services dropdown helpers
      aggregateServicesLabel,
      aggregateServicesBtnClass,
      amsLabel,
      pontyLabel,
      jobylonLabel,
      teamtailorLabel,
      intensoLabel,
      amsBadgeClass,
      serviceBtnClassPonty,
      serviceBtnClassJobylon,
      serviceBtnClassTeamtailor,
      intensoBadgeClass,
      openIntensoModal,
      closeIntensoModal,
      serviceBtnClassIntenso,
      updateJobTipService,
      addJobTipFilter,
      addRecmanFilter,
      openJobTipModal,
      closeJobTipModal,
      jobTipLabel,
      serviceBtnClassJobTip,
      openAmsModal,
      closeAmsModal,
      serviceBtnClassAms,
      addJobTipConnection,
      addIntensoConnection,
      adminAddJobTip,
      toggleForceIncomingExternalDraft,
      toggleJobFetchService,
      toggleIgnoreDemandSlots,
      setLogoSource,
      toggleSsoEnabled
    };
  }
})
